import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from './Header';
import WhitelistManager from './WhitelistManager';
import './AdminPanel.css';

function AdminPanel({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    is_admin: false
  });

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, [searchQuery, filterActive]);

  const fetchUsers = async () => {
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (filterActive !== 'all') params.active = filterActive === 'active';

      const response = await axios.get('/admin/users', { params });
      setUsers(response.data.users);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/admin/stats');
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        password: '',
        full_name: user.full_name,
        is_admin: user.is_admin
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        password: '',
        full_name: '',
        is_admin: false
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({ email: '', password: '', full_name: '', is_admin: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingUser) {
        // Ενημέρωση
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password; // Μην στέλνεις κενό password

        await axios.put(`/admin/users/${editingUser.id}`, updateData);
        alert('Ο χρήστης ενημερώθηκε επιτυχώς!');
      } else {
        // Δημιουργία
        await axios.post('/admin/users', formData);
        alert('Ο χρήστης δημιουργήθηκε επιτυχώς!');
      }
      
      handleCloseModal();
      fetchUsers();
      fetchStats();
    } catch (error) {
      alert(error.response?.data?.error || 'Σφάλμα κατά την αποθήκευση');
    }
  };

  const handleDelete = async (userId, userEmail) => {
    if (!window.confirm(`Είστε σίγουροι ότι θέλετε να διαγράψετε τον χρήστη ${userEmail};\n\nΑυτή η ενέργεια δεν μπορεί να αναιρεθεί!`)) {
      return;
    }

    try {
      await axios.delete(`/admin/users/${userId}`);
      alert('Ο χρήστης διαγράφηκε επιτυχώς!');
      fetchUsers();
      fetchStats();
    } catch (error) {
      alert(error.response?.data?.error || 'Σφάλμα κατά τη διαγραφή');
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      const endpoint = currentStatus ? 'deactivate' : 'activate';
      await axios.patch(`/admin/users/${userId}/${endpoint}`);
      fetchUsers();
      fetchStats();
    } catch (error) {
      alert(error.response?.data?.error || 'Σφάλμα κατά την ενημέρωση');
    }
  };

  if (loading) {
    return (
      <div>
        <Header user={user} onLogout={onLogout} />
        <div className="admin-container">
          <div className="loading">Φόρτωση...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header user={user} onLogout={onLogout} />
      
      <div className="admin-container">
        <div className="admin-header">
          <h1>👥 Διαχείριση Συστήματος</h1>
        </div>

        {/* Tabs */}
        <div className="admin-tabs">
          <button 
            className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            👤 Χρήστες
          </button>
          <button 
            className={`tab-button ${activeTab === 'whitelist' ? 'active' : ''}`}
            onClick={() => setActiveTab('whitelist')}
          >
            📧 Εγκεκριμένα Emails (SSO)
          </button>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'users' ? (
          <>
            <div className="admin-header" style={{marginTop: '20px'}}>
              <h2>Διαχείριση Χρηστών</h2>
              <button onClick={() => handleOpenModal()} className="btn-primary">
                + Νέος Χρήστης
              </button>
            </div>

        {stats && (
          <div className="stats-grid">
            <div className="stat-card">
              <h3>Σύνολο</h3>
              <p className="stat-number">{stats.total_users}</p>
            </div>
            <div className="stat-card active">
              <h3>Ενεργοί</h3>
              <p className="stat-number">{stats.active_users}</p>
            </div>
            <div className="stat-card inactive">
              <h3>Ανενεργοί</h3>
              <p className="stat-number">{stats.inactive_users}</p>
            </div>
            <div className="stat-card admin">
              <h3>Διαχειριστές</h3>
              <p className="stat-number">{stats.admin_users}</p>
            </div>
          </div>
        )}

        <div className="filters-section">
          <input
            type="text"
            placeholder="🔍 Αναζήτηση (email ή όνομα)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          
          <select 
            value={filterActive} 
            onChange={(e) => setFilterActive(e.target.value)}
            className="filter-select"
          >
            <option value="all">Όλοι</option>
            <option value="active">Ενεργοί</option>
            <option value="inactive">Ανενεργοί</option>
          </select>
        </div>

        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Email</th>
                <th>Ονοματεπώνυμο</th>
                <th>Ρόλος</th>
                <th>Κατάσταση</th>
                <th>Tasks</th>
                <th>Ημ/νία Εγγραφής</th>
                <th>Ενέργειες</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="8" className="no-data">Δεν βρέθηκαν χρήστες</td>
                </tr>
              ) : (
                users.map(u => (
                  <tr key={u.id} className={!u.is_active ? 'inactive-row' : ''}>
                    <td>{u.id}</td>
                    <td>{u.email}</td>
                    <td>{u.full_name}</td>
                    <td>
                      {u.is_admin ? (
                        <span className="badge badge-admin">Admin</span>
                      ) : (
                        <span className="badge badge-user">User</span>
                      )}
                    </td>
                    <td>
                      {u.is_active ? (
                        <span className="status-active">Ενεργός</span>
                      ) : (
                        <span className="status-inactive">Ανενεργός</span>
                      )}
                    </td>
                    <td>
                      <span title="Tasks που δημιούργησε">{u.tasks_created}</span> / 
                      <span title="Tasks που του έχουν ανατεθεί"> {u.tasks_assigned}</span>
                    </td>
                    <td>{new Date(u.created_at).toLocaleDateString('el-GR')}</td>
                    <td className="actions-cell">
                      <button
                        onClick={() => handleOpenModal(u)}
                        className="btn-edit"
                        title="Επεξεργασία"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleToggleActive(u.id, u.is_active)}
                        className={u.is_active ? 'btn-deactivate' : 'btn-activate'}
                        title={u.is_active ? 'Απενεργοποίηση' : 'Ενεργοποίηση'}
                      >
                        {u.is_active ? '🔒' : '🔓'}
                      </button>
                      <button
                        onClick={() => handleDelete(u.id, u.email)}
                        className="btn-delete"
                        title="Διαγραφή"
                        disabled={u.id === user.id}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {showModal && (
          <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>{editingUser ? 'Επεξεργασία Χρήστη' : 'Νέος Χρήστης'}</h2>
              
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Κωδικός {editingUser && '(αφήστε κενό για να μην αλλάξει)'}</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required={!editingUser}
                    minLength={6}
                  />
                </div>

                <div className="form-group">
                  <label>Ονοματεπώνυμο *</label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.is_admin}
                      onChange={(e) => setFormData({...formData, is_admin: e.target.checked})}
                    />
                    Διαχειριστής
                  </label>
                </div>

                <div className="modal-actions">
                  <button type="submit" className="btn-primary">
                    {editingUser ? 'Ενημέρωση' : 'Δημιουργία'}
                  </button>
                  <button type="button" onClick={handleCloseModal} className="btn-secondary">
                    Ακύρωση
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
          </>
        ) : (
          <WhitelistManager />
        )}
      </div>
    </div>
  );
}

export default AdminPanel;
